import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    FiLock, FiEye, FiEyeOff, FiAlertCircle, FiCheckCircle, FiArrowLeft,
} from 'react-icons/fi';
import {
    confirmPasswordResetApi, validatePasswordResetTokenApi,
} from '../../Api';
import { notify } from '../../components/Notify';

// Status do token: 'checking' (consulta inicial), 'ok' (válido),
// 'invalid' (não existe), 'used' (já usado), 'expired' (passou da hora),
// 'success' (senha trocada com sucesso).
export default function RedefinirSenha() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';

    const [tokenStatus, setTokenStatus] = useState('checking');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            setTokenStatus('invalid');
            return;
        }
        let cancelado = false;
        (async () => {
            try {
                const res = await validatePasswordResetTokenApi({ token });
                if (cancelado) return;
                if (res.data?.valid) {
                    setTokenStatus('ok');
                } else {
                    setTokenStatus(res.data?.error || 'invalid');
                }
            } catch (err) {
                if (!cancelado) setTokenStatus('invalid');
            }
        })();
        return () => { cancelado = true; };
    }, [token]);

    function validate() {
        const next = {};
        if (!password) next.password = 'Informe a nova senha.';
        else if (password.length < 6) next.password = 'A senha deve ter pelo menos 6 caracteres.';
        if (!confirm) next.confirm = 'Confirme a nova senha.';
        else if (confirm !== password) next.confirm = 'As senhas não conferem.';
        setErrors(next);
        return Object.keys(next).length === 0;
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (loading) return;
        if (!validate()) return;

        setLoading(true);
        try {
            await confirmPasswordResetApi({ token, password });
            setTokenStatus('success');
            notify('Senha redefinida com sucesso!', 'success');
            // Volta pro login depois de uma pequena pausa pra dar tempo
            // do usuário ler a tela de sucesso.
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            const msg = err.response?.data?.error || 'Erro ao redefinir senha.';
            notify(msg, 'error');
            // Se for um problema de token, atualiza o status pra desabilitar form.
            if (/expirou|já foi usado|inválido/i.test(msg)) {
                setTokenStatus(/expirou/i.test(msg) ? 'expirado' : /usado/i.test(msg) ? 'usado' : 'invalid');
            }
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.brand}>
                    <div style={styles.brandLogo}>U</div>
                    <div style={styles.brandText}>
                        <span style={styles.brandName}>UEN</span>
                        <span style={styles.brandTag}>Estudantil</span>
                    </div>
                </div>

                {tokenStatus === 'checking' && (
                    <div style={styles.centerBox}>
                        <span style={{ ...styles.spinner, borderTopColor: '#059669', borderColor: 'rgba(5,150,105,0.2)' }} />
                        <p style={styles.subtitle}>Validando o link…</p>
                    </div>
                )}

                {tokenStatus === 'ok' && (
                    <>
                        <h1 style={styles.title}>Redefinir senha</h1>
                        <p style={styles.subtitle}>Crie uma nova senha para sua conta UEN.</p>

                        <form onSubmit={handleSubmit} noValidate>
                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="password">Nova senha</label>
                                <div style={styles.inputWrap}>
                                    <span style={styles.inputIcon}><FiLock size={18} /></span>
                                    <input
                                        id="password"
                                        type={showPwd ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        placeholder="Mínimo 6 caracteres"
                                        style={{
                                            ...styles.input, paddingRight: 40,
                                            ...(errors.password ? styles.inputError : {}),
                                        }}
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (errors.password) setErrors(prev => ({ ...prev, password: undefined }));
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd(s => !s)}
                                        style={styles.eyeBtn}
                                        aria-label={showPwd ? 'Ocultar senha' : 'Mostrar senha'}
                                    >
                                        {showPwd ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <div style={styles.fieldError}>
                                        <FiAlertCircle size={14} /> {errors.password}
                                    </div>
                                )}
                            </div>

                            <div style={styles.field}>
                                <label style={styles.label} htmlFor="confirm">Confirme a nova senha</label>
                                <div style={styles.inputWrap}>
                                    <span style={styles.inputIcon}><FiLock size={18} /></span>
                                    <input
                                        id="confirm"
                                        type={showPwd ? 'text' : 'password'}
                                        autoComplete="new-password"
                                        placeholder="Digite novamente"
                                        style={{
                                            ...styles.input,
                                            ...(errors.confirm ? styles.inputError : {}),
                                        }}
                                        value={confirm}
                                        onChange={(e) => {
                                            setConfirm(e.target.value);
                                            if (errors.confirm) setErrors(prev => ({ ...prev, confirm: undefined }));
                                        }}
                                    />
                                </div>
                                {errors.confirm && (
                                    <div style={styles.fieldError}>
                                        <FiAlertCircle size={14} /> {errors.confirm}
                                    </div>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                style={{ ...styles.submit, opacity: loading ? 0.65 : 1 }}
                            >
                                {loading ? (
                                    <>
                                        <span style={styles.spinner} />
                                        Salvando...
                                    </>
                                ) : 'Redefinir senha'}
                            </button>
                        </form>

                        <Link to="/login" style={styles.backLink}>
                            <FiArrowLeft size={14} /> Voltar para o login
                        </Link>
                    </>
                )}

                {(tokenStatus === 'invalid' || tokenStatus === 'usado' || tokenStatus === 'expirado') && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#FEF2F2', color: '#DC2626' }}>
                            <FiAlertCircle size={36} />
                        </div>
                        <h1 style={styles.title}>
                            {tokenStatus === 'expirado' && 'Link expirado'}
                            {tokenStatus === 'usado' && 'Link já utilizado'}
                            {tokenStatus === 'invalid' && 'Link inválido'}
                        </h1>
                        <p style={styles.subtitle}>
                            {tokenStatus === 'expirado' && 'Este link expirou. Solicite um novo para redefinir sua senha.'}
                            {tokenStatus === 'usado' && 'Este link já foi utilizado. Se precisar trocar a senha novamente, solicite um novo.'}
                            {tokenStatus === 'invalid' && 'O link de redefinição é inválido ou está incompleto.'}
                        </p>
                        <Link to="/esqueci-senha" style={{ ...styles.submit, textDecoration: 'none', marginTop: 8 }}>
                            Solicitar novo link
                        </Link>
                        <Link to="/login" style={styles.backLink}>
                            <FiArrowLeft size={14} /> Voltar para o login
                        </Link>
                    </div>
                )}

                {tokenStatus === 'success' && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#ECFDF5', color: '#059669' }}>
                            <FiCheckCircle size={36} />
                        </div>
                        <h1 style={styles.title}>Senha redefinida!</h1>
                        <p style={styles.subtitle}>
                            Sua senha foi atualizada com sucesso. Você já pode entrar com a nova senha.
                        </p>
                        <Link to="/login" style={{ ...styles.submit, textDecoration: 'none', marginTop: 8 }}>
                            Ir para o login
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 60%, #F0FDFA 100%)',
        display: 'grid', placeItems: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: '#0F172A',
    },
    card: {
        width: '100%', maxWidth: 440, background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '40px 36px',
    },
    brand: {
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32,
    },
    brandLogo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: 18,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
    brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandName: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    brandTag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },

    title: {
        fontSize: '1.6rem', fontWeight: 700, color: '#0F172A',
        margin: '0 0 8px', letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#64748B', margin: '0 0 24px', fontSize: '0.92rem',
        lineHeight: 1.55,
    },

    field: { marginBottom: 20 },
    label: {
        display: 'block', fontSize: '0.78rem', fontWeight: 600,
        color: '#334155', marginBottom: 6,
    },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: {
        position: 'absolute', left: 14, color: '#94A3B8', pointerEvents: 'none',
        display: 'flex', alignItems: 'center',
    },
    input: {
        width: '100%', padding: '12px 14px 12px 42px',
        border: '1.5px solid #E2E8F0', borderRadius: 10,
        fontSize: '0.95rem', color: '#0F172A', background: '#FFFFFF',
        outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    },
    inputError: { borderColor: '#F43F5E' },
    eyeBtn: {
        position: 'absolute', right: 8, background: 'transparent',
        border: 'none', cursor: 'pointer', padding: 8, color: '#64748B',
        display: 'grid', placeItems: 'center', borderRadius: 6,
    },
    fieldError: {
        marginTop: 6, fontSize: '0.8rem', color: '#E11D48',
        display: 'flex', alignItems: 'center', gap: 4,
    },

    submit: {
        width: '100%', padding: '12px 18px',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff', border: 'none', borderRadius: 10,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, fontFamily: 'inherit',
    },
    spinner: {
        width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        marginTop: 22, color: '#059669', fontSize: '0.88rem',
        fontWeight: 600, textDecoration: 'none',
    },

    centerBox: { textAlign: 'center' },
    iconCircle: {
        width: 72, height: 72, borderRadius: '50%',
        display: 'grid', placeItems: 'center', margin: '0 auto 18px',
    },
};
